import {ChangeDetectionStrategy, Component, ElementRef, inject, input, linkedSignal, viewChild} from '@angular/core';
import {IconComponent} from '@shared/icon';
import {AccordionComponent} from './accordion.component';
import {AutoTabIndexDirective} from '../auto-tab-index-directive';

@Component({
	selector: 'dc-accordion-panel',
	standalone: true,
	imports: [IconComponent, AutoTabIndexDirective],
	template: `
		<button
			#headerButton
			type="button"
			class="panel-header"
			(click)="toggle($event)"
			(keydown.enter)="toggle($event)"
			(keydown.space)="toggle($event)"
			[attr.aria-expanded]="expanded()"
			[attr.aria-controls]="'panel-content-' + id()"
			autoTabIndex="tab"
		>
			<div class="panel-title">
				<ng-content select="[accordion-title]"></ng-content>
			</div>
			<div class="panel-indicators">
				<ng-content select="[accordion-indicator]"></ng-content>
				<icon [icon]="expanded() ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></icon>
			</div>
		</button>
		<div #content [id]="'panel-content-' + id()" class="panel-content" [class.expanded]="expanded()" [style.display]="expanded() ? 'block' : 'none'">
			<ng-content></ng-content>
		</div>
	`,
	styles: `
		:host {
			display: block;
			margin-bottom: 1rem;
			border: 1px solid #e2e8f0;
			border-radius: 0.5rem;
			box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.05);
			background-color: white;
			overflow: hidden;
			transition: box-shadow 0.3s ease;
		}

		:host:hover {
			box-shadow: 0 4px 6px 0 rgba(0, 0, 0, 0.07);
		}

		.panel-header {
			display: flex;
			justify-content: space-between;
			align-items: center;
			width: 100%;
			padding: 1.25rem 1.5rem;
			background-color: #f8fafc;
			cursor: pointer;
			user-select: none;
			border: none;
			text-align: right;
		}

		.panel-header:hover {
			background-color: #f1f5f9;
		}

		.panel-header:focus {
			outline: none;
			box-shadow: inset 0 0 0 2px rgba(59, 130, 246, 0.5);
		}

		.panel-title {
			margin: 0;
			font-size: 1.1rem;
			font-weight: 600;
			color: #334155;
		}

		.panel-indicators {
			display: flex;
			align-items: center;
			gap: 0.5rem;
		}

		.panel-indicators i {
			margin-right: 0.5rem;
			margin-left: 0.5rem;
			font-size: 0.875rem;
			color: #64748b;
			transition: transform 0.3s ease;
		}

		.panel-indicators .fa-chevron-up {
			transform: rotate(180deg);
		}

		.panel-content {
			padding: 1.5rem;
			border-top: 1px solid #e2e8f0;
			background-color: white;
		}

		.panel-content.expanded {
			animation: fadeIn 0.3s ease-in-out;
		}

		@keyframes fadeIn {
			from {
				opacity: 0;
				transform: translateY(-10px);
			}
			to {
				opacity: 1;
				transform: translateY(0);
			}
		}
	`,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccordionPanelComponent {
	/**
	 * מזהה ייחודי של הפאנל
	 */
	public readonly id = input<string>('');
	/**
	 * האם לפתוח את הפאנל בטעינה ראשונית
	 */
	public readonly initiallyExpanded = input<boolean>(false);
	/**
	 * האם הפאנל פתוח
	 */
	public readonly expanded = linkedSignal(() => this.initiallyExpanded());
	readonly contentElement = viewChild<ElementRef>('content');
	readonly headerButton = viewChild<ElementRef>('headerButton');
	private readonly accordion = inject(AccordionComponent, {optional: true});
	private lastClickTime = 0;

	/**
	 * פותח או סוגר את הפאנל
	 */
	public toggle(event: Event): void {
		event.preventDefault();

		// בדיקת דאבל קליק - מניעת קליקים מהירים מדי
		const now = Date.now();
		if (now - this.lastClickTime < 200) {
			return;
		}
		this.lastClickTime = now;

		const newExpandedState = !this.expanded();

		if (newExpandedState) {
			this.expand();
		} else {
			this.collapse();
		}
	}

	/**
	 * פותח את הפאנל
	 */
	public expand(): void {
		this.expanded.set(true);

		// יידע את האקורדיון האב שפאנל זה נפתח
		if (this.accordion) {
			this.accordion.openPanel(this);
		}
	}

	/**
	 * סוגר את הפאנל
	 */
	public collapse(): void {
		this.expanded.set(false);
	}
}
